import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle, ShieldCheck, Building2, Users2, GraduationCap, Ban, Undo2, MessageSquare, Mail } from "lucide-react";

const PRO_ACTIONS = [
  { key: "reinstate",         label: "Reinstate",       icon: Undo2,          cls: "bg-gold-500 hover:bg-gold-600 text-white" },
  { key: "warn",              label: "Issue warning",   icon: AlertTriangle,  cls: "bg-amber-500 hover:bg-amber-600 text-white" },
  { key: "require_training",  label: "Require training",icon: GraduationCap,  cls: "bg-navy-800 hover:bg-navy-900 text-white" },
  { key: "remove",            label: "Remove permanently", icon: Ban,         cls: "bg-red-600 hover:bg-red-700 text-white" },
];
const CO_ACTIONS = [
  { key: "reinstate",             label: "Reinstate",       icon: Undo2,          cls: "bg-gold-500 hover:bg-gold-600 text-white" },
  { key: "warn",                  label: "Issue warning",   icon: AlertTriangle,  cls: "bg-amber-500 hover:bg-amber-600 text-white" },
  { key: "require_improvements",  label: "Require improvements", icon: GraduationCap, cls: "bg-navy-800 hover:bg-navy-900 text-white" },
  { key: "suspend",               label: "Suspend",         icon: Ban,            cls: "bg-orange-600 hover:bg-orange-700 text-white" },
  { key: "remove",                label: "Remove permanently", icon: Ban,         cls: "bg-red-600 hover:bg-red-700 text-white" },
];

function ActionDialog({ entity, actions, path, onDone, kind }) {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState("");
  const [note, setNote] = useState("");
  const [blockBookings, setBlockBookings] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!action) return toast.error("Select an action");
    setBusy(true);
    try {
      await api.post(path, {
        action, note,
        ...(kind === "company" && action === "require_improvements" ? { block_bookings: blockBookings } : {}),
      });
      toast.success(`Action applied: ${action}`);
      setOpen(false);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-brand hover:bg-brand-hover text-white" size="sm" data-testid={`qa-open-${entity.id}`}>
          Review actions
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>QA Action — {entity.full_name || entity.company_name || entity.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-2">
            {actions.map((a) => (
              <button
                key={a.key} type="button" onClick={() => setAction(a.key)}
                data-testid={`qa-action-${a.key}-${entity.id}`}
                className={`flex items-center gap-2 rounded-md px-3 py-2 border text-left text-sm font-manrope font-semibold ${
                  action === a.key ? a.cls + " border-transparent" : "bg-[#0A0A0A] text-white border-white/10 hover:border-brand"
                }`}
              >
                <a.icon className="w-4 h-4" /> {a.label}
              </button>
            ))}
          </div>
          {kind === "company" && action === "require_improvements" && (
            <label className="flex items-center gap-2 text-sm font-manrope">
              <Checkbox checked={blockBookings} onCheckedChange={(v) => setBlockBookings(!!v)} />
              Block new bookings until resolved
            </label>
          )}
          <div>
            <Label>Note (sent to the {kind})</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid={`qa-note-${entity.id}`} placeholder="Explain the decision — this is included in the notification email." />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !action} className="bg-brand hover:bg-brand-hover text-white" data-testid={`qa-submit-${entity.id}`}>
            {busy ? "Applying…" : "Apply Action"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProReviewsPanel({ userId }) {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get(`/reviews/for/${userId}`).then((r) => setReviews(r.data || [])).catch(() => setReviews([])); }, [userId]);
  if (!reviews.length) return null;
  return (
    <details className="mt-3 text-xs font-manrope">
      <summary className="cursor-pointer text-brand font-semibold">{reviews.length} performance review{reviews.length > 1 ? "s" : ""}</summary>
      <div className="mt-2 space-y-2">
        {reviews.map((r) => (
          <div key={r.id} className="rounded-md border border-white/10 p-2 bg-[#0E0E0E]">
            <div className="text-white/85"><strong>{r.author_name}</strong> · Overall {r.overall_performance}/5 · {new Date(r.created_at).toLocaleDateString()}</div>
            {r.written && <div className="text-white/70 mt-1">&ldquo;{r.written}&rdquo;</div>}
          </div>
        ))}
      </div>
    </details>
  );
}

function AppealNote({ entity }) {
  const appeal = entity?.qa_appeal;
  if (!appeal) return null;
  return (
    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid={`qa-appeal-${entity.id}`}>
      <div className="text-[11px] uppercase tracking-widest text-amber-700 font-outfit font-bold flex items-center gap-1">
        <MessageSquare className="w-3 h-3" /> Appeal submitted
        {appeal.submitted_at && <span className="text-white/60 normal-case font-manrope font-normal">· {new Date(appeal.submitted_at).toLocaleString()}</span>}
      </div>
      <div className="text-sm text-white font-manrope mt-1 whitespace-pre-wrap">&ldquo;{appeal.text}&rdquo;</div>
    </div>
  );
}

function CompanyReviewsPanel({ companyId }) {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get(`/company-reviews/for/${companyId}`).then((r) => setReviews(r.data || [])).catch(() => setReviews([])); }, [companyId]);
  if (!reviews.length) return null;
  return (
    <details className="mt-3 text-xs font-manrope">
      <summary className="cursor-pointer text-brand font-semibold">{reviews.length} staff review{reviews.length > 1 ? "s" : ""}</summary>
      <div className="mt-2 space-y-2">
        {reviews.map((r) => (
          <div key={r.id} className="rounded-md border border-white/10 p-2 bg-[#0E0E0E]">
            <div className="text-white/85"><strong>{r.author_secusource_id}</strong> · Overall {r.overall_experience}/5 · {new Date(r.created_at).toLocaleDateString()}</div>
            {r.written && <div className="text-white/70 mt-1 whitespace-pre-wrap">&ldquo;{r.written}&rdquo;</div>}
          </div>
        ))}
      </div>
    </details>
  );
}

export default function AdminQA() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [companies, setCompanies] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/qa-flagged");
      setUsers((data.users || []).filter((u) => u.role !== "company"));
      setCompanies((data.users || []).filter((u) => u.role === "company"));
      setTeams(data.teams || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 justify-between items-start">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-1">Quality Assurance</div>
          <h1 className="text-3xl font-outfit font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-amber-500" /> Under Review
          </h1>
          <p className="text-white/60 mt-1 font-manrope text-sm">
            Every removal is a human decision. Review, warn, train — or remove — as appropriate. Standards apply equally to professionals and companies.
          </p>
        </div>
        <Button
          onClick={async () => {
            try {
              const { data } = await api.post("/admin/qa-digest/send");
              toast.success(`Digest sent — ${data.total} active items`);
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          className="bg-navy-900 hover:bg-navy-800 text-white"
          data-testid="qa-send-digest-btn"
        >
          <Mail className="w-4 h-4 mr-2" /> Send QA Digest
        </Button>
      </div>

      <Tabs defaultValue="pros">
        <TabsList>
          <TabsTrigger value="pros" data-testid="qa-tab-pros">
            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Professionals ({users.length})
          </TabsTrigger>
          <TabsTrigger value="teams" data-testid="qa-tab-teams">
            <Users2 className="w-3.5 h-3.5 mr-1" /> Teams ({teams.length})
          </TabsTrigger>
          <TabsTrigger value="companies" data-testid="qa-tab-companies">
            <Building2 className="w-3.5 h-3.5 mr-1" /> Companies ({companies.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pros" className="space-y-3 mt-4">
          {users.length === 0 && <div className="text-white/60 font-manrope">No professionals under review. Standards holding.</div>}
          {users.map((u) => (
            <div key={u.id} className="card-flat p-5 border-l-4 border-l-amber-400" data-testid={`qa-user-${u.secusource_id}`}>
              <div className="flex justify-between gap-3 flex-wrap items-start">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="ss-id">{u.secusource_id}</span>
                    <span className="pill pill-pending">Under Review</span>
                  </div>
                  <div className="font-outfit font-semibold text-white mt-1">{u.full_name}</div>
                  <div className="text-sm text-white/60 font-manrope">
                    {u.poor_review_count_12mo || 0} poor performance reviews in 12mo · flagged {u.qa_flagged_at ? new Date(u.qa_flagged_at).toLocaleDateString() : "-"}
                  </div>
                  <ProReviewsPanel userId={u.id} />
                  <AppealNote entity={u} />
                </div>
                <ActionDialog entity={u} actions={PRO_ACTIONS} path={`/admin/qa/user/${u.id}/action`} onDone={load} kind="professional" />
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="teams" className="space-y-3 mt-4">
          {teams.length === 0 && <div className="text-white/60 font-manrope">No teams under review.</div>}
          {teams.map((t) => (
            <div key={t.id} className="card-flat p-5 border-l-4 border-l-amber-400">
              <div className="flex justify-between gap-3 flex-wrap items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="ss-id">{t.lead_secusource_id}</span>
                    <span className="pill pill-pending">Under Review</span>
                  </div>
                  <div className="font-outfit font-semibold text-white mt-1">{t.name}</div>
                  <div className="text-sm text-white/60 font-manrope">{t.poor_review_count_12mo || 0} poor reviews · flagged {t.qa_flagged_at ? new Date(t.qa_flagged_at).toLocaleDateString() : "-"}</div>
                  <AppealNote entity={t} />
                </div>
                <ActionDialog entity={t} actions={PRO_ACTIONS} path={`/admin/qa/team/${t.id}/action`} onDone={load} kind="team" />
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="companies" className="space-y-3 mt-4">
          {companies.length === 0 && <div className="text-white/60 font-manrope">No companies under review.</div>}
          {companies.map((c) => (
            <div key={c.id} className="card-flat p-5 border-l-4 border-l-red-400" data-testid={`qa-company-${c.secusource_id}`}>
              <div className="flex justify-between gap-3 flex-wrap items-start">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="ss-id">{c.secusource_id}</span>
                    <span className="pill pill-rejected">{(c.qa_status || "under_review").replace(/_/g, " ")}</span>
                    {c.booking_blocked && <span className="pill pill-rejected">Bookings blocked</span>}
                  </div>
                  <div className="font-outfit font-semibold text-white mt-1">{c.company_name}</div>
                  <div className="text-sm text-white/60 font-manrope flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" />
                    {c.poor_review_count_12mo || 0} independent staff reported concerns in 12mo · flagged {c.qa_flagged_at ? new Date(c.qa_flagged_at).toLocaleDateString() : "-"}
                  </div>
                  <CompanyReviewsPanel companyId={c.id} />
                  <AppealNote entity={c} />
                </div>
                <ActionDialog entity={c} actions={CO_ACTIONS} path={`/admin/qa/company/${c.id}/action`} onDone={load} kind="company" />
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
